import type { Recipe } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'
import type { RecipeV2Doc } from '../queries/templates.ts'

/**
 * 레시피 편집기(V2) 쓰기 · 계산 — 새 형식(Opentrons protocol/8 · 편집층 `designerApplication.data`).
 *
 * 실물(`ctcells-core` `app/views/recipes_v2.py` · `views/recipes.py` · core@d50b8cf4)
 *
 * | 연산 | 경로 | 본문 | 응답 | 역할 |
 * | --- | --- | --- | --- | --- |
 * | 작업 명령 다시 만들기 | `POST /recipes/regenerate` | 문서 그대로 | 200 `{doc, same, error}` — 방출기가 멈추면 `doc`·`same` null + `error` | operator · admin |
 * | 새 레시피로 저장 | `POST /recipes` | `{command_id, name, schema_version, payload}` | 201 `Recipe` · 규칙 위반 422 `RECIPE_VALIDATION_FAIL`(`violations`) | operator · admin · 제어락 불요 |
 * | 새 버전 저장 | `PUT /recipes/{id}` | 같은 꼴 | 200 `Recipe`(version +1) · 실행 중이면 409 `RECIPE_IMMUTABLE` | 🔴 **제어락 필요**(`X-Control-Token` · 클라이언트가 붙인다) |
 *
 * - 🔴 다시 만들기는 **DB 무접촉 계산**이라 무효화할 것이 없다. 결과 문서는 편집기 상태로만 간다(캐시에 쓰지 않는다).
 * - 🔴 `PUT` 은 새 버전 **전체 교체**다 — 편집기는 받은 문서 위에 고친 칸만 얹은 «전체»를 보낸다(`editorModel.ts` 머리말).
 * - `schema_version` 은 문서의 `designerApplication.version`(쓰는 쪽이 밝힌다 · 백엔드 편집기 `saveBody` 와 같다).
 */

export interface RegenerateResult {
  doc: RecipeV2Doc | null
  same: boolean | null
  error: string | null
}

export const useRegenerateRecipeV2 = createCommand<{ doc: RecipeV2Doc }, RegenerateResult>({
  send: ({ doc }) =>
    orchestrator.request<RegenerateResult>({
      method: 'POST',
      path: '/recipes/regenerate',
      body: doc,
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [],
})

export interface SaveRecipeV2Vars {
  name: string
  doc: RecipeV2Doc
  schemaVersion: string
  commandId?: string
}

/** 새 레시피로 저장 — `POST /recipes`(제어락 불요) */
export const useCreateRecipeV2 = createCommand<SaveRecipeV2Vars, Recipe>({
  send: ({ name, doc, schemaVersion, commandId }) =>
    orchestrator.request<Recipe>({
      method: 'POST',
      path: '/recipes',
      body: {
        command_id: commandId ?? newCommandId(),
        name,
        schema_version: schemaVersion,
        payload: doc,
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.recipes.all()],
})

/** 새 버전 저장 — `PUT /recipes/{id}`. 🔴 제어락 대상(`controlToken.ts` 표 · 클라이언트가 `X-Control-Token` 을 붙인다) */
export const useSaveRecipeV2Version = createCommand<
  SaveRecipeV2Vars & { recipeId: string },
  Recipe
>({
  send: ({ recipeId, name, doc, schemaVersion, commandId }) =>
    orchestrator.request<Recipe>({
      method: 'PUT',
      path: `/recipes/${encodeURIComponent(recipeId)}`,
      body: {
        command_id: commandId ?? newCommandId(),
        name,
        schema_version: schemaVersion,
        payload: doc,
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: ({ recipeId }) => [keys.recipes.detail(recipeId), keys.recipes.all()],
})
